import {computed, ComputedRef, getCurrentInstance, onMounted, Ref, ref, watch} from '@vue/composition-api'

import ridoApi from '@/api/rido.api'
import {mapDataToComments, RemovableLogItem} from '@/mappers/comments'
import {Lead, LeadStatus} from '@/types'
import {mapSalesAppointmentsToCommentItems} from '@/utils/sales-appointment-comments'

interface Props {
  id: Ref<string> | ComputedRef<string> | string
}

export const useLead = ({id}: Props) => {
  const lead = ref<Lead | null>(null)
  const isLoading = ref(false)

  const commentLog = ref<RemovableLogItem[] | undefined>()
  const isCommentLogLoading = ref(false)

  const store = getCurrentInstance()?.proxy.$store

  const lastUpdatedByMe = computed(() => {
    return lead.value?.updatedBy?.includes(lead.value?.assignee as string)
  })

  onMounted(async () => {
    const {id: leadId} = mapProps({id})
    const [, appointmentsResult] = await Promise.all([
      fetchLead({id: leadId}),
      ridoApi.getLeadSalesAppointments(leadId).catch(error => {
        console.warn(`Error fetching sales appointments for lead ${leadId}`, error)
        store?.dispatch('showAlertError', 'common.error')
        return {data: []}
      }),
    ])

    const appointmentItems = mapSalesAppointmentsToCommentItems(appointmentsResult.data || [])
    const allComments = [...(lead.value?.commentLog || []), ...appointmentItems]
    allComments.sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : a.timestamp.valueOf()
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : b.timestamp.valueOf()
      return dateB - dateA
    })
    commentLog.value = mapDataToComments(allComments)
  })

  watch(commentLog, async (oldComments, newComments) => {
    if (newComments && oldComments && oldComments !== newComments) {
      await updateComments()
    }
  })

  function mapProps({id}: Props) {
    return {id: typeof id === 'string' ? id : id.value}
  }

  async function fetchLead({id}: {id: string | undefined}, showLoading = true, includeAppointments = false) {
    if (!id) {
      return Promise.resolve()
    }

    try {
      if (showLoading) {
        isLoading.value = true
      }

      const {data: leadResponse} = await ridoApi.getLead(id)
      lead.value = leadResponse

      if (includeAppointments) {
        try {
          const appointmentsResult = await ridoApi.getLeadSalesAppointments(id)
          const appointmentItems = mapSalesAppointmentsToCommentItems(appointmentsResult.data || [])
          const allComments = [...(leadResponse.commentLog || []), ...appointmentItems]
          allComments.sort((a, b) => {
            const dateA = a.dueDate ? new Date(a.dueDate).getTime() : a.timestamp.valueOf()
            const dateB = b.dueDate ? new Date(b.dueDate).getTime() : b.timestamp.valueOf()
            return dateB - dateA
          })
          commentLog.value = mapDataToComments(allComments)
        } catch (err) {
          console.warn(`Error fetching sales appointments for lead ${id}`, err)
        }
      }
    } catch (e) {
      console.error(`Error fetching lead ${id}`, e)
    } finally {
      if (showLoading) {
        isLoading.value = false
      }
    }
  }

  async function markAsRead() {
    try {
      const {id: leadId} = mapProps({id})
      return ridoApi.markAsRead(leadId, true)
    } catch (e) {
      console.error(`Error fetching offer ${id}`, e)
      return Promise.reject(e)
    }
  }

  async function maskLeadAsLost() {
    if (!lead.value) {
      return
    }

    try {
      isLoading.value = true
      await ridoApi.updateLeadStatus(lead.value.id || '', LeadStatus.Lost)
      lead.value.status = LeadStatus.Lost
    } catch (e) {
      console.error(`Error updating read state in lead ${lead.value?.id}`, e)
    } finally {
      isLoading.value = false
    }
  }

  async function updateComments() {
    if (!commentLog.value) {
      return
    }

    try {
      isCommentLogLoading.value = true

      let commentLogCopy = [...commentLog.value]
      commentLogCopy = commentLogCopy.filter(comment => comment.isRemovable !== false)

      const {id: leadId} = mapProps({id})
      await ridoApi.updateComments(leadId, mapDataToComments(commentLogCopy))
      await fetchLead({id: leadId}, false)

      if (lead.value?.status === LeadStatus.New) {
        lead.value.status = LeadStatus.InProgress
      }
    } catch (e) {
      console.error(`Error adding comment to lead`, e)
    } finally {
      isCommentLogLoading.value = false
    }
  }

  return {
    commentLog,
    data: lead,
    isCommentLogLoading,
    isLoading,
    lastUpdatedByMe,
    markAsRead,
    maskLeadAsLost,
    refetch: async () => await fetchLead(mapProps({id}), true, true),
    updateComments,
  }
}
